export const SERVICES = [
  {
    id: "construction",
    title: "Construction & Remodeling",
    subtitle: "Complete build solutions",
    desc: "Full-scope residential and commercial construction. From villa extensions to complete fit-outs, we deliver precision builds that stand for decades.",
    heroDescription: "End-to-end residential and commercial construction delivered by one accountable contractor in Dubai.",
    metaTitle: "Villa Construction & Remodeling in Dubai | Al Hadeeqa",
    metaDescription: "Full-scope villa construction, extensions & fit-outs in Dubai. 35+ years experience, ISO certified, 50+ in-house crew. Dubai Municipality licensed. Free assessment.",
    image: "/assets/images/construction-remodeling.jpg",
    tags: ["Villa Construction", "Extensions", "Fit-Out", "Remodeling"],
    summary: [
      "Al Hadeeqa has been delivering construction projects across Dubai since 2009. With over 35 years of combined experience and 50+ in-house crew members, we handle new villa builds, extensions, structural remodeling, interior fit-outs, and basement conversions as a single accountable contractor — no subcontracting chains.",
      "Whether the brief is a full new build, an additional floor, or a complete internal renovation, the approach is the same: disciplined site management, properly sequenced trades, and durable workmanship. We coordinate structural works, MEP, finishes, and authority approvals in-house so timelines stay clean and communication stays direct.",
    ],
    inclusions: [
      "New villa construction and complete builds",
      "Villa extensions and additional floors",
      "Interior fit-out and structural remodeling",
      "Basement and lower-ground floor conversions",
      "Commercial interior fit-outs and office builds",
      "Dubai Municipality drawing submissions and approvals",
    ],
    idealFor: [
      "Villa owners planning extensions or additional rooms",
      "Property investors building from the ground up",
      "Commercial tenants needing a full fit-out",
      "Owners who want one contractor managing the full scope from slab to handover",
    ],
    serviceCardsHeading: "What we build.",
    serviceCards: [
      {
        title: "Villa Construction & Extensions",
        eyebrow: "New builds and additions done properly.",
        description: "From foundation to handover, Al Hadeeqa manages new villa construction and extension projects across Dubai. We handle structural engineering coordination, municipality approvals, all construction trades, and final finishes — so the client deals with one team, not five contractors.",
        bullets: [
          "New villa and townhouse construction",
          "Single and double-storey extensions",
          "Staff quarters, boundary walls, and outbuildings",
          "Structural modifications to existing buildings",
        ],
        bestFor: "Villa owners adding space, investors building from scratch, or developers needing one contractor for the complete build.",
      },
      {
        title: "Interior Fit-Out & Remodeling",
        eyebrow: "The full scope from demo to final paint.",
        description: "Interior fit-out projects succeed when trades are sequenced correctly. We manage partition works, flooring, ceilings, joinery, tiling, plumbing, electrical, and painting under a single programme — preventing the delays and rework that come from coordinating multiple disconnected contractors.",
        bullets: [
          "Full villa and apartment fit-outs",
          "Kitchen and bathroom remodeling",
          "Commercial office and retail fit-outs",
          "Partition walls, ceilings, and flooring",
        ],
        bestFor: "Owners remodeling an existing property, tenants fitting out a commercial space, or anyone wanting a single contractor for the complete interior scope.",
      },
      {
        title: "Basement Conversions",
        eyebrow: "Turn unused space into valuable floor area.",
        description: "Basement conversions require careful coordination of excavation, shoring, waterproofing, and structural works. Because Al Hadeeqa provides all four in-house, the sequencing is controlled from the start — which reduces surprises and keeps the project on programme.",
        bullets: [
          "New basement excavation and structural works",
          "Conversion of existing basements to habitable use",
          "Waterproofing and drainage integrated from day one",
          "Combined shoring and dewatering where required",
        ],
        bestFor: "Villa owners wanting additional liveable space, projects requiring below-grade construction, or clients who want dewatering, shoring, and build managed by one team.",
      },
    ],
    trustHeading: "Why use a single contractor for your build?",
    trustPoints: [
      {
        title: "35+ years of UAE construction experience",
        body: "Al Hadeeqa was founded by Engr. Muhammad Ashraf Jan, who has been working in UAE construction since the 1990s. The company has delivered 500+ projects across Dubai, Abu Dhabi, Sharjah, and Ajman.",
      },
      {
        title: "50+ in-house crew — no subcontracting",
        body: "Every trade on your project is carried out by Al Hadeeqa's own licensed team. The people who quote are the people who build. No middlemen, no finger-pointing when something needs fixing.",
      },
      {
        title: "ISO certified and Dubai Municipality licensed",
        body: "ISO 9001:2015 (Quality), ISO 14001:2015 (Environmental), OHSAS 18001:2007 (Health & Safety), and Dubai Municipality contractor registration number 1064775.",
      },
      {
        title: "Authority approvals managed in-house",
        body: "We prepare and submit drawing packages for Dubai Municipality, DEWA, and other authorities. Clients don't need to manage separate consultants — approvals are part of the scope.",
      },
    ],
    processHeading: "From brief to handover.",
    processSteps: [
      {
        number: "01",
        title: "Free Site Assessment",
        body: "We visit your property, review your brief, and assess site conditions, existing structure, and any authority constraints relevant to your project.",
      },
      {
        number: "02",
        title: "Design Coordination & Approvals",
        body: "We coordinate with structural and architectural consultants, prepare drawings, and handle Dubai Municipality submission and approval — all included in the scope.",
      },
      {
        number: "03",
        title: "Construction",
        body: "Structural works, MEP installation, and finishes carried out by our in-house crew on a clear programme. Progress is tracked and communicated throughout.",
      },
      {
        number: "04",
        title: "Handover",
        body: "Final snagging, quality checks, and documentation. We don't hand over until the work is complete and meets our own standards — not just the client's checklist.",
      },
    ],
    relatedIds: ["glassrooms", "waterproofing", "maintenance"],
  },
  {
    id: "shoring",
    title: "Shoring",
    subtitle: "Excavation support engineered for stability",
    desc: "Excavation support systems that stabilize open cuts and basement works safely. We coordinate shoring with dewatering and excavation so structural support is in place before the heavy work begins.",
    heroDescription: "Excavation support solutions that stabilize the dig, protect adjacent conditions, and prepare the site for safe progress.",
    metaTitle: "Shoring & Earth Retention in Dubai | Al Hadeeqa",
    metaDescription: "Sheet piling, soldier piles & shoring systems for deep excavations in Dubai. Combined shoring, dewatering & excavation from one contractor. Free site assessment.",
    image: "/assets/images/shoring.jpeg",
    gallery: [
      "/assets/images/rentals/shoring-1.webp",
      "/assets/images/rentals/shoring-2.webp",
      "/assets/images/rentals/shoring-3.webp",
    ],
    tags: ["Sheet Piling", "Retaining Systems", "Basement Support", "Excavation Safety"],
    summary: [
      "In Dubai's urban environment, any excavation deeper than 1.5–2m in granular soils — or any dig close to an adjacent structure — requires a properly engineered shoring system. Without it, walls collapse, neighbouring buildings settle, and utility lines shift. Shoring is not a precaution; it is a safety and legal requirement.",
      "Al Hadeeqa provides shoring as part of a coordinated enabling package alongside dewatering and excavation. Planning all three together — rather than sourcing them separately — is what keeps sites stable, dry, and on programme throughout the groundworks phase.",
    ],
    inclusions: [
      "Sheet piling installation and extraction",
      "Soldier pile and timber lagging systems",
      "Trench box systems for utility works",
      "Shotcrete wall installation",
      "Structural engineer coordination and monitoring",
      "Combined shoring, dewatering, and excavation packages",
    ],
    idealFor: [
      "Basement construction requiring vertical cut support",
      "Excavations adjacent to existing villa boundary walls or buildings",
      "Underground shelter and swimming pool construction",
      "Urban sites where open-cut excavation is not viable",
    ],
    serviceCardsHeading: "Shoring methods we use.",
    serviceCards: [
      {
        title: "Sheet Piling",
        eyebrow: "The most common method for urban Dubai sites.",
        description: "Steel sheet piles are driven into the ground to create an interlocking retaining wall before excavation begins. This method is ideal for sites with constrained boundaries, high water tables, and adjacent structures. Piles can be extracted and reused after construction, or left in place as permanent retention.",
        bullets: [
          "Vibratory hammer installation",
          "Suitable for sandy and silty soils typical of Dubai",
          "Can be used alongside dewatering systems",
          "Works well in coastal and high water table areas",
        ],
        bestFor: "Basement construction, underground shelter installation, and any excavation within 3m of a neighbouring structure or boundary wall.",
      },
      {
        title: "Soldier Piles & Timber Lagging",
        eyebrow: "Flexible support for varied site conditions.",
        description: "Steel H-piles are drilled or driven at regular intervals, with horizontal timber planks (lagging) inserted between them as excavation proceeds. This method offers flexibility for sites with variable soil conditions or where sheet piling access is restricted.",
        bullets: [
          "Suitable for sites with obstructions at depth",
          "Flexible panel-by-panel installation as dig proceeds",
          "Lower noise than vibratory piling on sensitive sites",
          "Can be combined with ground anchors for additional support",
        ],
        bestFor: "Sites with mixed soil conditions, restricted access for piling equipment, or where step-by-step installation is preferred.",
      },
      {
        title: "Combined Shoring & Dewatering",
        eyebrow: "One team for both — cleaner sequencing, fewer surprises.",
        description: "Shoring and dewatering work best when planned together. Al Hadeeqa provides both services in-house, which means groundwater drawdown and lateral earth support are designed to complement each other. This prevents the coordination gaps that commonly cause delays when the two services are sourced separately.",
        bullets: [
          "Integrated shoring and wellpoint dewatering",
          "Single programme for all enabling groundworks",
          "Dewatering started before shoring to reduce groundwater pressure",
          "Continuous coordination between shoring and excavation crews",
        ],
        bestFor: "Basement and underground shelter projects in coastal Dubai zones (Jumeirah, Palm, Marina) where both groundwater and lateral earth support must be managed simultaneously.",
      },
    ],
    trustHeading: "Why one contractor for shoring and dewatering matters.",
    trustPoints: [
      {
        title: "Shoring and dewatering planned as one package",
        body: "Most shoring problems are water problems. Al Hadeeqa designs both systems together — groundwater drawdown reduces soil pressure on the shoring, which means lighter, faster, and more cost-effective retention. Separate contractors don't do this.",
      },
      {
        title: "In-house equipment and operators",
        body: "We own our piling and dewatering equipment and operate it with trained crews. No third-party plant hire delays, no unfamiliar equipment on your site.",
      },
      {
        title: "Structural engineer coordination included",
        body: "Shoring designs are reviewed and approved by a qualified structural engineer. We manage the coordination as part of the project scope — not as an add-on you need to procure separately.",
      },
      {
        title: "Combined with excavation for single mobilisation",
        body: "Shoring, dewatering, and excavation in one package means one mobilisation, one programme, and one point of contact. Less coordination, less downtime between phases.",
      },
    ],
    processHeading: "From site assessment to safe excavation.",
    processSteps: [
      {
        number: "01",
        title: "Site Assessment",
        body: "We assess excavation depth, proximity to existing structures, soil conditions, and groundwater level to determine the appropriate shoring method and whether dewatering is required.",
      },
      {
        number: "02",
        title: "Design & Engineering",
        body: "Shoring layout and structural calculations are prepared and reviewed. We coordinate with the structural engineer and prepare any drawings required for authority submission.",
      },
      {
        number: "03",
        title: "Installation",
        body: "Sheet piles or soldier piles are installed before excavation begins. Dewatering system is set up in parallel. The sequence is controlled so the site is stable and dry before any open dig.",
      },
      {
        number: "04",
        title: "Monitor & Handover",
        body: "Shoring is monitored throughout the excavation phase. Once the structure is complete and backfilled, temporary piles are extracted if specified. Site is handed over ready for next phase.",
      },
    ],
    relatedIds: ["dewatering", "excavation", "demolition"],
  },
  {
    id: "waterproofing",
    title: "Waterproofing",
    subtitle: "Sealed against the elements",
    desc: "Villa roof, basement, bathroom & metal roof waterproofing in Dubai. Construction-grade systems built to last 10–25 years.",
    heroDescription: "Al Hadeeqa delivers construction-grade waterproofing for villas, basements, wet areas, and metal roofs across Dubai. Systems built to last 10–25 years — not just until the next rain.",
    metaTitle: "Waterproofing Dubai | Al Hadeeqa Contracting",
    metaDescription: "Villa roof, basement, bathroom & metal roof waterproofing in Dubai. Construction-grade systems with 10-year warranty. Free inspection. Al Hadeeqa Contracting — 35+ years in UAE.",
    image: "/assets/images/waterproofing.jpg",
    tags: ["Villa Roofs", "Basements", "Wet Areas", "Metal Roofs", "10-Year Warranty"],
    summary: [
      "Most waterproofing companies apply surface treatments. Al Hadeeqa is a licensed construction contractor with 35+ years in the UAE. We understand concrete, steel, drainage, and structural movement — so we fix the cause, not just the symptom.",
      "Our service covers all four primary waterproofing needs: villa flat roofs, basement and below-slab systems, bathroom and wet area tanking, and metal roof sealing. One company, one warranty, done properly.",
    ],
    inclusions: [
      "Villa flat roof and terrace waterproofing",
      "Basement and below-slab waterproofing",
      "Bathroom, kitchen, and wet area tanking",
      "Metal roof and warehouse waterproofing",
    ],
    idealFor: [
      "Villas with flat roofs showing staining, dampness, or active leaks",
      "Properties with basement garages or habitable lower-ground floors",
      "Renovation projects requiring bathroom or balcony retanking",
      "Warehouses and commercial buildings with metal roofing",
    ],
    pricingFrom: "200 AED / sqm",
    urgencyBanner: {
      text: "Post-Storm Assessment Available — After this week's heavy rainfall, we're offering free roof and basement inspections across Dubai. Water damage worsens quickly — don't wait for the next storm.",
      cta: "Book Your Free Inspection",
      waText: "Hi Al Hadeeqa, I'd like to book a free post-storm roof/basement inspection.",
    },
    serviceCardsHeading: "What we waterproof.",
    trustHeading: "Why choose a construction company for waterproofing?",
    processHeading: "From inspection to warranty.",
    serviceCards: [
      {
        title: "Villa Roof Waterproofing",
        eyebrow: "The most common request after heavy rain.",
        description: "Flat roofs in Dubai take punishment from both UV heat and sudden downpours. Cracked membranes, failed joints around AC units, and blocked drainage cause leaks that damage ceilings, electrics, and interiors. We strip the failed system, repair the substrate, and apply a new waterproofing membrane with heat-reflective topcoat.",
        bullets: [
          "Torch-applied bituminous membranes",
          "Liquid-applied polyurethane coatings",
          "Heat-reflective cool-roof topcoats",
          "Joint and penetration detailing",
        ],
        warranty: "Up to 10 years",
        bestFor: "Villas with flat roofs showing staining, dampness, or active leaks.",
      },
      {
        title: "Basement & Below-Slab Waterproofing",
        eyebrow: "We build underground shelters. We know how to keep water out.",
        description: "Basement waterproofing requires understanding hydrostatic pressure, soil conditions, and structural movement — not just surface coatings. Al Hadeeqa's experience in underground construction means your basement gets the same engineering attention.",
        bullets: [
          "External membrane application (new builds)",
          "Crystalline waterproofing (permanently blocks concrete pores)",
          "Injection grouting for active leaks",
          "Negative-side coatings for existing basements",
        ],
        bestFor: "Villas with basement garages, storage areas, or habitable lower-ground floors showing dampness or water ingress.",
      },
      {
        title: "Bathroom & Wet Area Waterproofing",
        eyebrow: "Prevent leaks before they become your neighbour's problem.",
        description: "Wet area failures are the #1 cause of water damage in apartments and villas. We apply tanking membranes under tiles in bathrooms, kitchens, laundry rooms, and balconies — before or after tile removal.",
        bullets: [
          "Cementitious tanking membranes",
          "Flexible liquid-applied coatings",
          "Floor drain and threshold detailing",
          "Balcony and planter waterproofing",
        ],
        bestFor: "Renovation projects, leaking bathrooms, new-build wet areas, balcony and planter boxes.",
      },
      {
        title: "Metal Roof & Warehouse Waterproofing",
        eyebrow: "Stop the drip before it damages your stock.",
        description: "Metal roofs in Dubai's industrial areas corrode at joints, screws, and gutters. We seal joints, treat rust, and apply elastomeric coatings that flex with thermal expansion.",
        bullets: [
          "Joint and screw waterproofing",
          "Rust treatment and prevention",
          "Elastomeric coating systems",
          "Gutter waterproofing and repair",
        ],
        warranty: "Up to 10 years",
        bestFor: "Warehouses, labour camps, factories, and commercial buildings with metal roofing.",
      },
    ],
    trustPoints: [
      {
        title: "We understand structure, not just coatings",
        body: "Most waterproofing companies apply surface treatments. We're a licensed construction contractor with 35+ years in the UAE. We understand concrete, steel, drainage, and structural movement — so we fix the cause, not just the symptom.",
      },
      {
        title: "One team, no subcontracting",
        body: "Our in-house crew handles every phase. The team that inspects is the team that builds. No middlemen, no finger-pointing.",
      },
      {
        title: "Warranty-backed systems",
        body: "Up to 10-year warranty on select waterproofing works. ISO 9001, ISO 14001, and OHSAS 18001 certified. Dubai Economy licensed.",
      },
      {
        title: "Free site assessment",
        body: "We inspect, diagnose, and quote before any work begins. No obligation, no pressure.",
      },
    ],
    processSteps: [
      {
        number: "01",
        title: "Free Inspection",
        body: "We visit your property, assess the condition, and identify all areas needing waterproofing.",
      },
      {
        number: "02",
        title: "System Recommendation",
        body: "We recommend the right system for your situation — membrane, coating, crystalline, or injection — and provide a clear, itemised quote.",
      },
      {
        number: "03",
        title: "Professional Application",
        body: "Our licensed crew prepares the surface, applies the system, and details every edge, joint, and penetration properly.",
      },
      {
        number: "04",
        title: "Handover & Warranty",
        body: "Water testing, documentation, and warranty certificate. Maintenance guidance included.",
      },
    ],
    relatedIds: ["construction", "maintenance", "glassrooms"],
    relatedArticle: {
      href: "/blog/dubai-floods-march-2026-villa-waterproofing-checklist",
      title: "Dubai's March 2026 Floods: What Villa Owners Should Inspect Right Now",
      desc: "After the March 26–28 storms, this checklist covers what to inspect — roof drains, ceiling stains, basement walls — and when to call a professional waterproofing contractor.",
      published: "29 March 2026",
    },
  },
  {
    id: "excavation",
    title: "Excavation",
    subtitle: "Precision earthworks",
    desc: "Controlled excavation for foundations, basements, utilities, and civil works. Our experienced operators handle complex sites with precision and disciplined safety protocols.",
    heroDescription: "Controlled excavation works that prepare the ground properly for structure, utilities, and next-stage construction.",
    metaTitle: "Excavation Services in Dubai | Foundation & Basement | Al Hadeeqa",
    metaDescription: "Foundation, basement & utility excavation in Dubai. In-house operators and equipment, combined with shoring & dewatering. ISO certified. Free site assessment.",
    image: "/assets/images/excavation.jpg",
    tags: ["Basement Dig", "Foundation Work", "Utility Trenching", "Site Prep"],
    summary: [
      "Al Hadeeqa provides controlled excavation services across Dubai using in-house equipment and experienced operators. From single-basement residential digs to deep underground shelter excavations, we handle the full scope — site clearing, bulk earthworks, precise foundation trenching, and spoil removal.",
      "Excavation on urban Dubai plots is rarely straightforward. High groundwater, tight boundaries, adjacent structures, and hard coral rock layers all create challenges that require proper planning and the right equipment. We assess each site and coordinate excavation with shoring and dewatering where required, so the dig proceeds safely and without delay.",
    ],
    inclusions: [
      "Bulk and mass excavation",
      "Basement and foundation trenching",
      "Swimming pool and underground shelter excavation",
      "Utility trenching and service corridors",
      "Site levelling and earthworks preparation",
      "Spoil removal and disposal to approved sites",
    ],
    idealFor: [
      "Villa basement construction requiring a clean, shored excavation",
      "Swimming pool and underground shelter projects",
      "Foundation preparation for new builds and extensions",
      "Projects requiring integrated excavation, shoring, and dewatering",
    ],
    serviceCardsHeading: "Excavation types we handle.",
    serviceCards: [
      {
        title: "Basement & Foundation Excavation",
        eyebrow: "The most common residential scope.",
        description: "Basement excavations in Dubai require accurate levels, controlled edges, and coordination with shoring and dewatering systems. We use tracked excavators and bobcats to work within villa plot constraints, maintaining safe open faces and correct formation levels throughout.",
        bullets: [
          "Single and double basement levels",
          "Accurate formation levels for structural slabs",
          "Coordination with shoring where required",
          "Combined dewatering to maintain dry working conditions",
        ],
        bestFor: "New villa basements, underground shelter construction, and foundation preparation for extensions and new builds.",
      },
      {
        title: "Swimming Pool & Shelter Excavation",
        eyebrow: "Tight tolerances, clean formation.",
        description: "Pool and shelter excavations require tight dimensional control and careful bottom preparation. Over-excavation is costly; under-excavation delays the next trade. Our operators are experienced with villa-scale excavations where access is limited and working adjacent to existing structures.",
        bullets: [
          "Precise excavation to pool and shelter dimensions",
          "Clean, flat formation levels for base slab",
          "Dewatering as required for coastal villa sites",
          "Minimal site disturbance to surrounding landscaping",
        ],
        bestFor: "Residential swimming pools, underground shelter projects, and any excavation requiring precise dimensional control in a constrained villa plot.",
      },
      {
        title: "Utility Trenching & Site Earthworks",
        eyebrow: "Enabling works done as part of the broader programme.",
        description: "Utility trenching, site clearance, and bulk earthworks are often the first phase of a construction programme. We handle these enabling works with our own equipment and crew — so the main construction contractor gets a clear, levelled, properly prepared site on day one.",
        bullets: [
          "Drainage, service duct, and utility trenching",
          "Site clearing and vegetation removal",
          "Bulk cut-and-fill and platform preparation",
          "Backfilling and compaction using in-house compactors",
        ],
        bestFor: "Construction site preparation, infrastructure works, and any project requiring combined earthworks and utility trenching.",
      },
    ],
    trustHeading: "Why excavation and enabling works should come from one team.",
    trustPoints: [
      {
        title: "In-house equipment — no third-party plant",
        body: "Al Hadeeqa owns its excavator fleet, dump trucks, and compaction equipment. No dependency on external plant hire, no mobilisation delays waiting for third-party contractors.",
      },
      {
        title: "Combined dewatering and shoring capability",
        body: "We provide excavation, dewatering, and shoring in-house. When all three are planned together, sites stay drier, edges stay stable, and the dig proceeds without coordination gaps.",
      },
      {
        title: "Experienced operators on complex sites",
        body: "Our operators understand Dubai's soil conditions — sandy fill, silty coastal layers, and coral rock. They know how to maintain safe faces, read formation levels, and work adjacent to existing structures without incident.",
      },
      {
        title: "Spoil removal and disposal included",
        body: "All excavated material is loaded into our dump trucks and disposed of at approved Dubai sites. One contractor, one invoice, one programme from break-ground to clear site.",
      },
    ],
    processHeading: "From site assessment to clear, level ground.",
    processSteps: [
      {
        number: "01",
        title: "Site Assessment",
        body: "We assess soil conditions, groundwater depth, access for plant, proximity to structures, and whether shoring or dewatering is required before excavation can begin.",
      },
      {
        number: "02",
        title: "Planning & Coordination",
        body: "Excavation programme is set. Shoring and dewatering are designed in parallel where required. Equipment is confirmed and access routes planned.",
      },
      {
        number: "03",
        title: "Excavation",
        body: "Bulk dig, foundation trenching, or pool/shelter excavation carried out by our operators. Formation levels are checked throughout. Spoil is removed from site daily.",
      },
      {
        number: "04",
        title: "Handover to Next Trade",
        body: "Excavation handed over with correct levels, clean formation, and dewatered conditions maintained. Site is ready for structural slab, foundation, or next-phase construction.",
      },
    ],
    relatedIds: ["dewatering", "shoring", "construction"],
  },
  {
    id: "dewatering",
    title: "Dewatering",
    subtitle: "Groundwater control from day one",
    desc: "Professional dewatering systems for basements, foundations, and deep excavations. We keep sites dry, safe, and productive with wellpoint systems, pumping setups, and active groundwater management.",
    heroDescription: "Groundwater control systems that keep excavation work moving safely and reduce downstream site risk.",
    metaTitle: "Dewatering Services in Dubai | Construction Dewatering | Al Hadeeqa",
    metaDescription: "Professional dewatering for basements, foundations & deep excavations in Dubai. Wellpoint systems, deep well & sump pumping. Combined with shoring & excavation. Free assessment.",
    image: "/assets/images/dewatering.png",
    gallery: [
      "/assets/images/rentals/dewatering-1.webp",
      "/assets/images/rentals/dewatering-2.webp",
      "/assets/images/rentals/dewatering-3.webp",
    ],
    tags: ["Wellpoint Systems", "Groundwater Control", "Deep Excavation", "Site Pumping"],
    summary: [
      "In Dubai's coastal and low-lying areas — Jumeirah, Palm Jumeirah, Dubai Marina, and Business Bay — groundwater is encountered at 1.5–3m below surface. Without active dewatering, any excavation into that zone becomes unstable, unsafe, and unworkable. Al Hadeeqa has been managing groundwater on Dubai construction sites for 15+ years.",
      "We provide wellpoint systems, deep well dewatering, and sump pumping as a standalone service or as part of a combined enabling package with shoring and excavation. Handling all three in-house means the dewatering system is designed to complement the shoring design — which reduces soil pressure on the retaining system and keeps the whole dig more stable.",
    ],
    inclusions: [
      "Wellpoint dewatering systems for shallow to medium depths",
      "Deep well dewatering for excavations below 6m",
      "Sump pumping for open excavations",
      "Generator-backed systems for continuous operation",
      "Discharge management and environmental compliance",
      "Combined dewatering with shoring and excavation packages",
    ],
    idealFor: [
      "Basement and foundation excavations in high water table areas",
      "Underground shelter construction in coastal Dubai zones",
      "Swimming pool excavations on coastal villa plots",
      "Projects requiring combined dewatering, shoring, and excavation from one contractor",
    ],
    serviceCardsHeading: "Dewatering methods we use.",
    serviceCards: [
      {
        title: "Wellpoint Dewatering",
        eyebrow: "The most widely used method for Dubai residential sites.",
        description: "Wellpoint systems use closely spaced small-diameter wells connected to a header pipe and vacuum pump. They draw groundwater down uniformly across the excavation area, typically achieving a 4–6m drawdown. This is the standard method for residential basement and pool excavations in Dubai's sandy coastal soils.",
        bullets: [
          "Effective to 4–6m drawdown depth",
          "Well-suited to Dubai's sandy and silty soils",
          "Quick to install (1–2 days) and remove",
          "Generator-backed for continuous 24hr operation",
        ],
        bestFor: "Residential basement excavations, swimming pool construction, and underground shelter projects in coastal Dubai areas with a high water table.",
      },
      {
        title: "Deep Well Dewatering",
        eyebrow: "For excavations requiring drawdown beyond 6m.",
        description: "Where wellpoints cannot achieve the required drawdown, deep well systems use larger-diameter boreholes with submersible pumps to extract groundwater from deeper aquifer layers. Each well operates independently, providing robust and reliable dewatering for deep basement and underground structure projects.",
        bullets: [
          "Suitable for drawdowns beyond 6m",
          "Each well independently pumped",
          "Lower noise and vibration than vibratory wellpoints",
          "Monitoring data available for engineer review",
        ],
        bestFor: "Commercial basement projects, multi-level underground structures, and any excavation requiring drawdown below the reach of conventional wellpoint systems.",
      },
      {
        title: "Combined Dewatering, Shoring & Excavation",
        eyebrow: "One team for all three — no coordination gaps.",
        description: "Al Hadeeqa provides dewatering, shoring, and excavation in-house. On most Dubai basement and underground structure projects, these three scopes are interdependent — dewatering reduces groundwater pressure on the shoring, shoring allows vertical cuts without battering, and excavation proceeds with a consistently dry formation. Sourcing them separately introduces coordination gaps that delay programmes.",
        bullets: [
          "Integrated design — dewatering and shoring planned together",
          "Single point of contact for all enabling groundworks",
          "One mobilisation, one programme, one invoice",
          "Faster handover to structural contractor",
        ],
        bestFor: "Any Dubai project requiring basement construction, underground shelter, or deep excavation in areas with a high water table.",
      },
    ],
    trustHeading: "Why Al Hadeeqa for dewatering in Dubai.",
    trustPoints: [
      {
        title: "15+ years in Dubai's groundwater conditions",
        body: "We have dewatered sites across Jumeirah, Palm Jumeirah, Dubai Marina, JBR, Business Bay, and other coastal areas. We know the soil profiles, typical water table levels, and the right systems for each zone.",
      },
      {
        title: "Own equipment fleet — no third-party dependency",
        body: "Al Hadeeqa owns its wellpoint pumps, header pipes, submersible pumps, and generators. Equipment is maintained in-house and mobilises on our programme — not on a hire company's schedule.",
      },
      {
        title: "Combined with shoring and excavation in-house",
        body: "Dewatering works best when the shoring designer knows the dewatering plan. Because we provide both, the two systems are designed to complement each other — which improves stability and reduces the total cost of enabling works.",
      },
      {
        title: "Discharge management and compliance",
        body: "All dewatering discharge is managed in accordance with Dubai Municipality environmental requirements. We handle discharge point coordination and any necessary compliance documentation.",
      },
    ],
    processHeading: "From groundwater assessment to dry site.",
    processSteps: [
      {
        number: "01",
        title: "Site Review",
        body: "We assess the excavation depth, soil type, proximity to the coast, and available data on local groundwater levels. This determines whether wellpoint, deep well, or sump pumping is appropriate.",
      },
      {
        number: "02",
        title: "System Design",
        body: "Wellpoint spacing, pump capacity, and discharge route are designed. If shoring is also required, the two systems are designed together so they complement each other.",
      },
      {
        number: "03",
        title: "Installation & Activation",
        body: "Wellpoints or deep wells are installed. Header pipes and pumps are connected and tested. Drawdown begins 24–48 hours before excavation starts to allow groundwater to stabilise at the target level.",
      },
      {
        number: "04",
        title: "Active Management & Handover",
        body: "System runs continuously throughout excavation and foundation phases. Pump performance is monitored. Once the structure is waterproofed and backfilled, the system is decommissioned and equipment removed.",
      },
    ],
    relatedIds: ["shoring", "excavation", "construction"],
  },
  {
    id: "carports",
    title: "Premium Carports",
    subtitle: "Protection worthy of the machine",
    desc: "Your Lamborghini, Ferrari, or Rolls deserves more than a parking spot. Our architectural carports are precision-engineered shelters for exceptional vehicles.",
    heroDescription: "High-spec carport builds designed to protect premium vehicles while matching the architecture around them.",
    metaTitle: "Custom Carports in Dubai | Supercar & Luxury Vehicle Shelters | Al Hadeeqa",
    metaDescription: "Architectural carports for luxury villas in Dubai. Steel, aluminium & louvred roofing. Supercar clearances, UV protection, DM approvals managed. Free on-site quote.",
    image: "/assets/images/carport-main.jpg",
    tags: ["Premium Ready", "UV-Resistant", "Custom Steel", "10-Year Warranty"],
    summary: [
      "Dubai's sun reaches surface temperatures that strip paint, fade interiors, and accelerate UV degradation faster than almost anywhere else. A standard parking shade does the minimum. An architectural carport is a precision structure designed around the vehicle, the property, and the owner's standard.",
      "Al Hadeeqa builds carports as architectural components of the property — not bolt-on additions. We design for clearance height (important for SUVs and high-roof vehicles), drainage, UV-rated roofing materials, structural wind-load compliance, and visual alignment with the villa's facade. The result is a shelter that protects the vehicle and improves the frontage.",
    ],
    inclusions: [
      "Custom footprints for 1 to 6+ vehicles",
      "Steel or aluminium structural frame",
      "UV-resistant and heat-reflective roofing",
      "High-clearance designs for SUVs and elevated vehicles",
      "Drainage integration and perimeter detailing",
      "Optional LED lighting and louvred roof systems",
      "Dubai Municipality drawing submission and approval management",
    ],
    idealFor: [
      "Luxury villa owners protecting supercars, SUVs, and collector vehicles",
      "Properties where the carport is a visible part of the frontage",
      "Owners wanting UV protection beyond what a standard shade structure provides",
      "Multi-vehicle households needing a custom footprint and clearance design",
    ],
    serviceCardsHeading: "Carport types and materials.",
    serviceCards: [
      {
        title: "Steel Carports",
        eyebrow: "Maximum structural strength for wide spans.",
        description: "Powder-coated steel is the go-to structural material for carports spanning multiple vehicles. Steel handles wider spans without intermediate columns, which is important for supercar and SUV parking where door swing and access matter. All steel is hot-dip galvanised before powder coating for corrosion resistance in Dubai's humid coastal air.",
        bullets: [
          "Wide spans without intermediate columns",
          "Powder-coated in any RAL colour",
          "Hot-dip galvanised for coastal climate durability",
          "Suitable for flat, pitched, and mono-pitch rooflines",
        ],
        bestFor: "Multi-vehicle carports (3+), wide-span designs, and properties where a heavier structural profile complements the architecture.",
      },
      {
        title: "Aluminium & Louvred Carports",
        eyebrow: "Lightweight, corrosion-resistant, and design-forward.",
        description: "Powder-coated aluminium offers a cleaner, lighter aesthetic than steel with excellent resistance to Dubai's UV and humidity. Louvred roof systems — where adjustable aluminium slats can be opened or closed — are the premium option, providing full weather protection when closed and ventilation when open.",
        bullets: [
          "Powder-coated aluminium in any RAL colour",
          "Motorised louvre roofs: open for ventilation, close for rain",
          "Integrated drainage in louvre channel systems",
          "Clean, contemporary aesthetic for modern villas",
        ],
        bestFor: "Premium villa frontages, clients wanting a design-forward solution, and owners who want the versatility of a louvred roof system.",
      },
      {
        title: "Polycarbonate & Tensile Fabric Roofing",
        eyebrow: "Diffused light, no heat spike.",
        description: "Polycarbonate multi-wall panels transmit natural light while blocking UV — important for vehicles where cabin temperatures and paint fading are the primary concern. Tensile fabric canopies are an alternative for a softer, more organic aesthetic, with high-UV-resistant HDPE or PVC membrane options.",
        bullets: [
          "Multi-wall polycarbonate — diffused light, UV block",
          "HDPE and PVC tensile fabric canopies",
          "Significantly cooler cabin temperatures than opaque steel",
          "Available in tinted and heat-reflective grades",
        ],
        bestFor: "Vehicle storage where cabin temperature and UV protection are the primary requirement, or where a lighter aesthetic is preferred over solid roofing.",
      },
    ],
    trustHeading: "Why choose Al Hadeeqa for your carport.",
    trustPoints: [
      {
        title: "Designed around the vehicle, not just the spec",
        body: "We start with what you park. Supercar low-profile? Clearance is tight. Three full-size SUVs? Column placement matters. Collector with a vintage car? No condensation or drip risk. The carport is designed for the actual vehicles, not a generic template.",
      },
      {
        title: "Construction contractor, not a fabrication shop",
        body: "Al Hadeeqa is a licensed Dubai construction contractor with structural engineering capability. Carports are designed for UAE wind loads, anchored properly into the slab or ground, and built to last — not just to look good on day one.",
      },
      {
        title: "DM approvals managed in-house",
        body: "Dubai Municipality approval is required for permanent carport structures. We handle the drawing preparation and submission. Clients don't need to engage a separate consultant or chase approvals themselves.",
      },
      {
        title: "10-year structural warranty",
        body: "All Al Hadeeqa carport builds come with a 10-year structural warranty. ISO 9001:2015 certified quality management means quality checks are built into the process, not applied at the end.",
      },
    ],
    processHeading: "From site visit to handover.",
    processSteps: [
      {
        number: "01",
        title: "Free Site Visit",
        body: "We visit your property, review the available footprint, and discuss your vehicles, aesthetic preferences, and budget. We measure the space and photograph the frontage for design reference.",
      },
      {
        number: "02",
        title: "Design & Approval",
        body: "A 3D design is prepared for your review. Once approved, we prepare drawings for Dubai Municipality submission and manage the approval process on your behalf.",
      },
      {
        number: "03",
        title: "Fabrication",
        body: "Steel or aluminium is cut, welded, and powder-coated at our facility. Roofing panels or louvre systems are ordered to specification. Typical fabrication time is 2–3 weeks.",
      },
      {
        number: "04",
        title: "Installation & Handover",
        body: "Foundation or anchor bolts are installed first. The frame and roof are erected, drainage is connected, and optional lighting is wired. Handover includes a structural check and care instructions.",
      },
    ],
    relatedIds: ["pergolas", "construction", "maintenance"],
  },
  {
    id: "pergolas",
    title: "Luxury Pergolas",
    subtitle: "Crafted for the exceptional",
    desc: "Bespoke pergolas engineered for UAE's elite. Powder-coated aluminum, louvred roofing, integrated lighting - spaces that become destinations.",
    heroDescription: "Architectural pergolas designed to elevate outdoor living without compromising on durability in the UAE climate.",
    metaTitle: "Custom Pergolas in Dubai | Luxury Aluminium Pergolas | Al Hadeeqa",
    metaDescription: "Luxury custom pergolas in Dubai — louvred, fixed-roof, rooftop & garden. Powder-coated aluminium, DM approvals, integrated lighting. Free design consultation.",
    image: "/assets/images/pergola.jpg",
    tags: ["Louvred Roof", "Custom Design", "Lighting", "DM Approved"],
    summary: [
      "Al Hadeeqa has designed and built custom pergolas across Dubai's premium villa communities since 2009. We work in powder-coated aluminium, structural steel, and treated timber — selected based on the aesthetic, the site, and how the structure will be used. Every pergola is designed for Dubai's climate: UV-rated, wind-load compliant, and properly drained.",
      "Pergola construction is one of the areas where being a full construction contractor — rather than just a fabrication shop — matters. We understand foundations, structural loads, wall anchoring, and the authority approval process. That means the structure is built to last and the DM permit is managed without the client having to coordinate it separately.",
    ],
    inclusions: [
      "Custom pergola design and 3D visualisation",
      "Powder-coated aluminium, steel, and timber options",
      "Motorised louvre and fixed-roof systems",
      "Integrated LED lighting and ceiling fans",
      "Drainage channels and rainwater management",
      "Privacy screens and side panels",
      "Dubai Municipality drawing submission and approval",
    ],
    idealFor: [
      "Villa garden and pool deck areas needing a permanent shade structure",
      "Rooftop terraces requiring a DM-approved covered space",
      "Outdoor dining and entertaining areas",
      "Properties where the pergola is a visible design feature of the outdoor space",
    ],
    serviceCardsHeading: "Pergola types we build.",
    serviceCards: [
      {
        title: "Motorised Louvre Pergolas",
        eyebrow: "The premium choice for Dubai's climate.",
        description: "Motorised louvre pergolas have adjustable aluminium slats that open and close via remote control or a smart home system. When closed, they provide full protection from sun and rain. When open, they allow air circulation and natural light. The system integrates integrated LED lighting, drainage channels, and optional side screens.",
        bullets: [
          "Remote-controlled or smart home integration",
          "Full rain and sun protection when closed",
          "Ventilation and natural light when open",
          "Integrated drainage in the louvre channels",
        ],
        warranty: "Up to 10 years",
        bestFor: "Villa owners who want full year-round usability from their outdoor space — open for evenings, closed for midday heat or unexpected rain.",
      },
      {
        title: "Fixed-Roof Pergolas",
        eyebrow: "Permanent shade, clean design.",
        description: "Fixed-roof pergolas use a solid or semi-transparent roofing panel — powder-coated steel, aluminium, polycarbonate, or tensile fabric — mounted on an aluminium or steel frame. Simpler than louvre systems, they provide consistent shade without mechanical components, making them lower maintenance and more cost-effective.",
        bullets: [
          "Aluminium and steel frame options",
          "Solid, polycarbonate, or fabric roof panels",
          "Custom pitch and drainage integration",
          "Optional integrated lighting",
        ],
        bestFor: "Garden seating areas, covered walkways, and outdoor dining spaces where consistent shade is the primary requirement and ventilation is secondary.",
      },
      {
        title: "Timber & Steel Pergolas",
        eyebrow: "Character and warmth for garden settings.",
        description: "Hardwood and treated timber pergolas offer a natural aesthetic that suits landscaped villa gardens. We use timber for open-beam pergolas with climbing plants, or combine a timber cladded frame with a louvre roof for a warmer look. Steel pergolas with powder-coated finishes offer a more industrial, contemporary aesthetic.",
        bullets: [
          "Hardwood, cedar, and treated pine options",
          "Timber facade over aluminium structural frame",
          "Steel powder-coated in any RAL colour",
          "Open-beam or partially covered designs",
        ],
        bestFor: "Landscaped garden settings where natural materials complement the planting scheme, or modern villas where a statement steel structure is the design intent.",
      },
    ],
    trustHeading: "Why Al Hadeeqa for your pergola.",
    trustPoints: [
      {
        title: "Built to Dubai's climate standards, not European ones",
        body: "Pergolas sold in Dubai from European catalogues are not designed for 50°C+ summers, 90% humidity, and the UV index that bleaches untreated materials in months. Every Al Hadeeqa pergola is specified for UAE conditions from the ground up.",
      },
      {
        title: "DM approvals managed — no extra consultant needed",
        body: "Dubai Municipality requires approved drawings for permanent outdoor structures. We prepare and submit the drawings as part of the project. Clients don't engage a separate consultant or chase approvals — it's included.",
      },
      {
        title: "Construction contractor, not just a fabricator",
        body: "Al Hadeeqa is a licensed Dubai construction contractor. Pergola foundations, wall anchoring, and structural loads are properly engineered — not just bolted on and hoped for the best.",
      },
      {
        title: "2–4 weeks from design approval to installation",
        body: "Most residential pergola projects complete in 2–4 weeks after design is approved. We manage fabrication, delivery, and installation on a single programme with no gaps.",
      },
    ],
    processHeading: "From consultation to completed pergola.",
    processSteps: [
      {
        number: "01",
        title: "Free Site Visit & Design Brief",
        body: "We visit your property, review the available space, discuss your brief (shade only, fully enclosed, rooftop, garden), material preferences, and budget. Measurements and photos taken on-site.",
      },
      {
        number: "02",
        title: "3D Design & DM Submission",
        body: "A 3D design is prepared for your approval. Once you're happy, we prepare engineering drawings and submit to Dubai Municipality. Approval is managed by Al Hadeeqa — typically 1–2 weeks.",
      },
      {
        number: "03",
        title: "Fabrication",
        body: "Aluminium or steel is cut, welded, and powder-coated to specification. Louvre systems and lighting components are ordered. Fabrication typically takes 1–2 weeks once design is approved.",
      },
      {
        number: "04",
        title: "Installation & Handover",
        body: "Foundation anchors are installed first. Frame is erected, roof panels or louvre system fitted, lighting wired and tested. Drainage checked. Handover includes a demonstration of the louvre controls and care instructions.",
      },
    ],
    relatedIds: ["carports", "glassrooms", "maintenance"],
  },
  {
    id: "demolition",
    title: "Demolition",
    subtitle: "Cleared. Clean. Ready.",
    desc: "Safe, licensed demolition for residential and commercial structures. Full debris removal and site clearance leave your plot ready for what comes next.",
    heroDescription: "Licensed demolition and clearance work that resets the site cleanly for redevelopment, excavation, or rebuild.",
    metaTitle: "Demolition Services in Dubai | Licensed Demolition Contractor | Al Hadeeqa",
    metaDescription: "Licensed residential & commercial demolition in Dubai. Full villa demolition, selective strip-out, debris removal. DM permits managed. Free site assessment.",
    image: "/assets/images/demolition.jpg",
    tags: ["Licensed", "Debris Removal", "Structural Demo", "Site Clearance"],
    summary: [
      "Demolition in Dubai requires a permit from Dubai Municipality, a licensed contractor, and in most cases a structural engineer's confirmation that the method is safe for adjacent structures. Al Hadeeqa manages all of this as part of its demolition service — clients don't need to coordinate separately with a consultant or chase approvals.",
      "We approach demolition as the first phase of whatever comes next. That means the sequence is planned around what follows — excavation, new construction, or site redevelopment — so the cleared site is genuinely ready for the next contractor, not just an empty plot with debris pushed to the side.",
    ],
    inclusions: [
      "Full structural demolition of villas, buildings, and outbuildings",
      "Selective and partial demolition",
      "Interior strip-out and fit-out removal",
      "Demolition of boundary walls, shades, and carports",
      "Debris removal and disposal to approved sites",
      "Dubai Municipality demolition permit management",
    ],
    idealFor: [
      "Plots being cleared for full redevelopment or new villa construction",
      "Older structures reaching end of useful life",
      "Commercial spaces needing a full strip-out before new fit-out",
      "Projects needing a combined demolition and excavation programme",
    ],
    serviceCardsHeading: "Demolition types we handle.",
    serviceCards: [
      {
        title: "Full Structural Demolition",
        eyebrow: "Complete villa and building teardown.",
        description: "Full demolition of residential villas and commercial buildings is managed from permit application through to final site clearance. We use hydraulic excavators with demolition attachments, breakers, and bobcats to take structures down in a controlled sequence, protecting adjacent properties and utilities throughout.",
        bullets: [
          "Residential villa demolition (G+1 and larger)",
          "Commercial building and warehouse demolition",
          "Controlled sequence to protect adjacent structures",
          "All debris loaded and removed from site",
        ],
        bestFor: "Properties being cleared for full redevelopment — new villa construction, commercial development, or major extensions that require the existing structure to be removed first.",
      },
      {
        title: "Selective & Partial Demolition",
        eyebrow: "Remove what needs to go, keep what needs to stay.",
        description: "Selective demolition requires care and precision. Whether removing a single floor, an extension, an internal partition system, or a specific structural element, the work must be planned to ensure the remaining structure stays stable and undamaged. Al Hadeeqa handles the structural assessment and demolition as a single scope.",
        bullets: [
          "Partial floor or storey removal",
          "Selective wall and column removal",
          "Removal of extensions and additions",
          "Opening formation in existing structural walls",
        ],
        bestFor: "Properties being reconfigured, renovations requiring structural changes, or projects where only part of the existing structure is being replaced.",
      },
      {
        title: "Interior Strip-Out",
        eyebrow: "Back to concrete, ready for your new fit-out.",
        description: "Commercial tenants and villa owners undertaking major renovations often need a complete strip-out before new works begin. We remove all existing fit-out elements — partitions, ceilings, flooring, MEP fixtures, and joinery — and clear the space back to the structural shell, ready for the new design.",
        bullets: [
          "Complete fit-out removal including partitions and ceilings",
          "Flooring, tiling, and screed removal",
          "MEP fixture disconnection and removal",
          "Debris removed from site on the same day",
        ],
        bestFor: "Commercial tenants fitting out a new space, villa owners undertaking a full renovation, and offices being stripped back for reconfiguration.",
      },
    ],
    trustHeading: "Why Al Hadeeqa for demolition in Dubai.",
    trustPoints: [
      {
        title: "DM permits managed — we handle the process",
        body: "Dubai Municipality demolition permits require approved drawings, a licensed contractor declaration, and sometimes a structural engineer's report. Al Hadeeqa manages the entire permit process. Clients don't need to coordinate with a separate consultant.",
      },
      {
        title: "Licensed contractor with full safety compliance",
        body: "Al Hadeeqa holds ISO 9001, ISO 14001, and OHSAS 18001 certifications. All demolition is carried out under a formal method statement with dust suppression, noise management, and neighbour protection measures.",
      },
      {
        title: "Debris removal and disposal included",
        body: "All demolition waste is loaded into dump trucks and disposed of at approved Dubai sites. The project handover is a clean, cleared site — not a plot with rubble piled in the corner.",
      },
      {
        title: "Sequenced for what comes next",
        body: "We plan demolition around the following scope. If excavation follows, the sequence is planned to clear access for plant. If new construction follows immediately, demolition is timed to match the build programme.",
      },
    ],
    processHeading: "From permit to clear site.",
    processSteps: [
      {
        number: "01",
        title: "Survey & Permit Application",
        body: "We survey the structure, photograph existing conditions, and prepare the Dubai Municipality demolition permit application. Utility disconnection coordination is included.",
      },
      {
        number: "02",
        title: "Preparation",
        body: "Utilities are confirmed disconnected. Adjacent structures and boundaries are protected. Dust suppression and access routes are set up. Demolition sequence is reviewed.",
      },
      {
        number: "03",
        title: "Demolition",
        body: "Structure is taken down in a controlled sequence using hydraulic excavators, breakers, and bobcats. Debris is sorted and loaded for removal throughout the process.",
      },
      {
        number: "04",
        title: "Clearance & Handover",
        body: "All debris removed from site. Ground is cleared to the agreed level. Site is photographed and handed over ready for the next phase — excavation, new construction, or development.",
      },
    ],
    relatedIds: ["excavation", "shoring", "construction"],
  },
  {
    id: "glassrooms",
    title: "Glass Rooms & Partitions",
    subtitle: "Transparency, perfected",
    desc: "Architectural glass installations for offices, patios, balustrades, and bespoke glass rooms that blend indoor and outdoor living.",
    heroDescription: "Glass installations that open up space, improve flow, and keep detailing sharp across residential and commercial interiors.",
    metaTitle: "Glass Rooms & Partitions in Dubai | Al Hadeeqa Contracting",
    metaDescription: "Frameless glass rooms, office partitions & balustrades in Dubai. Tempered, laminated & sliding glass. Residential & commercial. DM approvals handled. Free quote.",
    image: "/assets/images/glass-rooms.jpg",
    tags: ["Frameless Glass", "Partitions", "Balustrades", "Enclosures"],
    summary: [
      "Glass installations are unforgiving. Misaligned panels, incorrect glass specifications, and poor drainage detailing become immediately visible — and in Dubai's sun, any compromise in glass quality shows within months. Al Hadeeqa builds glass rooms, enclosures, partitions, and balustrades to a construction standard — not a shopfitting standard.",
      "We use tempered safety glass, laminated glass, and double-glazed units, specifying the correct grade for the application: outdoor enclosures, office partitions, pool balustrades, or shower screens. Every installation is surveyed, fabricated to exact measurements, and detailed at every edge and joint.",
    ],
    inclusions: [
      "Frameless and semi-frameless glass rooms and patio enclosures",
      "Sliding and folding glass wall systems",
      "Office and commercial glass partition systems",
      "Frameless glass balustrades and pool fencing",
      "Glass balcony and terrace enclosures",
      "Shower screens and bathroom enclosures",
      "Dubai Municipality approval coordination for permanent enclosures",
    ],
    idealFor: [
      "Villa patios being converted into weather-protected living spaces",
      "Office and commercial spaces requiring partitioned zones with natural light",
      "Balconies and pool areas requiring frameless glass balustrades",
      "Properties wanting glass as a design element rather than just a divider",
    ],
    serviceCardsHeading: "Glass installations we build.",
    serviceCards: [
      {
        title: "Glass Rooms & Patio Enclosures",
        eyebrow: "Turn your outdoor space into a year-round room.",
        description: "Glass rooms enclose an existing patio, terrace, or garden area to create a protected living space. In Dubai, this is particularly effective for the 6-month cooler season — the space is usable from October to April without cooling, and the sliding or folding glass opens it up fully in the evenings. All permanent glass enclosures are designed for DM approval.",
        bullets: [
          "Aluminium framed with fixed and operable glass panels",
          "Sliding and folding door integration",
          "Tempered or laminated safety glass",
          "Roof options: glass, polycarbonate, or solid panel",
        ],
        bestFor: "Villa owners wanting to extend usable indoor space into the garden or patio area, or properties where a covered, climate-controlled outdoor room adds genuine living value.",
      },
      {
        title: "Office & Commercial Glass Partitions",
        eyebrow: "Open plan without the noise.",
        description: "Glass partitions separate office zones while maintaining natural light and visual connection. Frameless systems use 12–15mm tempered glass with minimal hardware for a clean, contemporary look. Framed aluminium systems offer more acoustic performance and can incorporate doors, pass-throughs, and integrated blinds.",
        bullets: [
          "Frameless 12–15mm tempered glass systems",
          "Framed aluminium partition systems",
          "Acoustic and fire-rated glass options",
          "Full-height, floor-to-ceiling installation",
        ],
        bestFor: "Commercial tenants, office fit-outs, meeting room partitions, retail spaces, and any commercial application requiring light-filled zone separation.",
      },
      {
        title: "Frameless Glass Balustrades",
        eyebrow: "Safety without blocking the view.",
        description: "Frameless glass balustrades use 12–15mm toughened glass panels fixed to the floor or fascia with minimal stainless steel hardware. They meet UAE building code load requirements while preserving sightlines on balconies, staircases, pool surrounds, and roof terraces. No posts, no rails — just glass.",
        bullets: [
          "Floor-fixed and fascia-fixed systems",
          "Toughened glass to UAE safety standards",
          "Minimal stainless steel hardware",
          "Pool fencing, staircase, and balcony applications",
        ],
        bestFor: "Balconies, pool surrounds, staircases, and roof terraces where the view is part of the design intent and traditional railing would obstruct it.",
      },
    ],
    trustHeading: "Why construction-grade glass installation matters.",
    trustPoints: [
      {
        title: "Glass specified for the application",
        body: "Outdoor enclosures, pool balustrades, and office partitions all require different glass grades. We specify tempered, laminated, or double-glazed units correctly for each application — not a one-size approach.",
      },
      {
        title: "Fabricated to exact site measurements",
        body: "Every glass panel is measured and fabricated to the specific site dimensions. We don't use standard-size panels cut to fit — custom fabrication means tighter joints, cleaner lines, and better weatherproofing.",
      },
      {
        title: "DM approvals for permanent enclosures",
        body: "Permanent glass enclosures attached to a villa or commercial property require Dubai Municipality approval. Al Hadeeqa manages drawing preparation and permit submission — included in the project scope.",
      },
      {
        title: "Combined with aluminium, pergola, and construction works",
        body: "Glass installations often sit alongside pergola structures, aluminium cladding, or interior construction. Al Hadeeqa can manage all of these in a single scope, which keeps the sequencing clean and avoids gaps between trades.",
      },
    ],
    processHeading: "From survey to clean installation.",
    processSteps: [
      {
        number: "01",
        title: "Survey & Specification",
        body: "We survey the site and take precise measurements. The correct glass type, frame system, and hardware are specified for the application and the DM requirements.",
      },
      {
        number: "02",
        title: "Fabrication",
        body: "Glass panels are cut and toughened to exact measurements. Frame profiles and hardware are prepared. Typical fabrication time is 1–2 weeks.",
      },
      {
        number: "03",
        title: "Installation",
        body: "Panels are installed with correct joint clearances, edge detailing, and drainage management. Every joint is sealed and every edge is checked for alignment before sign-off.",
      },
      {
        number: "04",
        title: "Handover",
        body: "Final check of all glass, hardware, and seals. Sliding or folding systems are tested for smooth operation. Care and maintenance instructions provided.",
      },
    ],
    relatedIds: ["construction", "pergolas", "maintenance"],
  },
  {
    id: "maintenance",
    title: "Maintenance",
    subtitle: "Ongoing care, on schedule",
    desc: "Planned and reactive maintenance for residential and commercial properties. Painting, repairs, fixture replacement, and day-to-day upkeep handled by one trusted team.",
    heroDescription: "Reliable maintenance support for owners who want one contractor handling recurring property issues and upkeep.",
    metaTitle: "Building Maintenance Services in Dubai | Al Hadeeqa Contracting",
    metaDescription: "Villa & commercial property maintenance in Dubai. Painting, repairs, waterproofing upkeep, annual contracts. Responds within 2 hrs. Al Hadeeqa — 35+ years in UAE.",
    image: "/assets/images/maintenance.jpg",
    tags: ["Annual Contracts", "Painting", "Repairs", "Handyman Services"],
    summary: [
      "Properties in Dubai age faster than in most climates. UV exposure degrades paint and sealants in 2–3 years. Roof membranes crack under thermal cycling. AC condensation causes moisture damage at penetrations. Tile grout fails in wet areas. Maintenance is not optional — it is the difference between a property that retains its value and one that deteriorates steadily.",
      "Al Hadeeqa provides planned and reactive maintenance for villas and commercial properties across Dubai. One contractor across multiple trades — painting, waterproofing repairs, structural crack repair, carpentry, tiling, and general repairs. Annual maintenance contracts give owners a predictable schedule and priority response for reactive issues.",
    ],
    inclusions: [
      "Interior and exterior painting",
      "Structural crack repair and replastering",
      "Waterproofing repairs and leak investigation",
      "Roof maintenance and drainage clearing",
      "Tile, grout, and sealant replacement",
      "Carpentry, door, and window repairs",
      "Annual maintenance contracts with scheduled visits",
    ],
    idealFor: [
      "Villa owners wanting one contractor for recurring upkeep across all trades",
      "Commercial property managers needing reliable maintenance response",
      "Property investors in Dubai requiring annual maintenance to protect asset value",
      "Clients who completed a project with Al Hadeeqa and want the same team for ongoing care",
    ],
    serviceCardsHeading: "Maintenance services we provide.",
    serviceCards: [
      {
        title: "Painting & Surface Maintenance",
        eyebrow: "The most visible maintenance investment.",
        description: "Exterior paint in Dubai lasts 2–4 years under UV exposure before chalking, fading, and surface cracking. Interior paint wears at junctions, door frames, and high-traffic areas. We use quality paints suited to Dubai's climate: UV-resistant exterior grades and moisture-resistant finishes for wet areas. All surfaces are properly prepared before application — no painting over failing paint.",
        bullets: [
          "Interior and exterior villa painting",
          "UV-resistant exterior paint systems",
          "Surface preparation and primer application",
          "Feature walls, boundary walls, and outbuildings",
        ],
        bestFor: "Annual exterior repaint cycles, interior refreshes, and any property where faded or peeling paint needs to be properly addressed.",
      },
      {
        title: "Structural Repairs & Waterproofing Maintenance",
        eyebrow: "Catch it early. It costs less.",
        description: "Structural cracks, failed waterproofing membranes, and active leaks all worsen with time in Dubai's climate. Hairline cracks can become structural issues within a few seasons of thermal cycling. Failed roof membranes cause ceiling damage within months. We diagnose the cause, repair properly, and re-waterproof where needed — not just fill and repaint.",
        bullets: [
          "Structural crack investigation and repair",
          "Waterproofing membrane repair and patching",
          "Roof and terrace maintenance",
          "Balcony and wet area sealant replacement",
        ],
        bestFor: "Properties with active leaks, visible cracking, or failing waterproofing that needs proper remediation rather than cosmetic treatment.",
      },
      {
        title: "Annual Maintenance Contracts",
        eyebrow: "Predictable budget. Priority response. One team.",
        description: "Annual maintenance contracts provide scheduled inspections, preventive maintenance visits, and priority response for reactive issues. For villa owners, this is the most cost-effective approach — problems are caught before they become expensive, and the maintenance team is familiar with the property from previous visits.",
        bullets: [
          "Two scheduled inspection visits per year",
          "Priority response for reactive issues (24–48 hrs)",
          "Documented condition reports after each visit",
          "Single annual invoice covering most maintenance items",
        ],
        bestFor: "Absentee villa owners, property investors, and commercial properties that benefit from predictable maintenance costs and documented property condition.",
      },
    ],
    trustHeading: "Why one contractor for all maintenance trades.",
    trustPoints: [
      {
        title: "Responds within 2 hours on WhatsApp",
        body: "All maintenance enquiries receive a WhatsApp response within 2 hours during business hours. Emergency reactive maintenance can typically be attended within 24–48 hours in Dubai.",
      },
      {
        title: "The same crew that built it, maintains it",
        body: "If Al Hadeeqa built your pergola, carport, or fit-out, the same team maintains it. They know the materials, the fixings, and the design intent — which means faster diagnosis and better repair quality.",
      },
      {
        title: "Licensed contractor, not a handyman service",
        body: "Al Hadeeqa is a Dubai Municipality licensed construction contractor with ISO certifications. Structural repairs, waterproofing remediation, and plaster works are done to the same standard as new construction.",
      },
      {
        title: "One team across all trades",
        body: "Painting, waterproofing, carpentry, tiling, and structural repairs — all from one contractor. No coordinating multiple trade companies, no gaps in accountability, no argument about whose fix caused the next problem.",
      },
    ],
    processHeading: "How maintenance works with Al Hadeeqa.",
    processSteps: [
      {
        number: "01",
        title: "Reactive or Scheduled Visit",
        body: "Contact us via WhatsApp with a description and photos. We respond within 2 hours, confirm the visit time, and attend within 24–48 hours for reactive issues, or on the scheduled date for planned maintenance.",
      },
      {
        number: "02",
        title: "Inspection & Diagnosis",
        body: "We inspect the reported issue and any related areas — leaks often have sources different from where water appears. A written diagnosis and quote is provided before any work begins.",
      },
      {
        number: "03",
        title: "Repair Works",
        body: "Maintenance is carried out by our in-house crew using the correct materials for each trade. No shortcuts, no cosmetic fixes over failing substrates.",
      },
      {
        number: "04",
        title: "Documentation & Follow-Up",
        body: "Photos of the completed work are shared with the client. Annual contract clients receive a condition report. Any issues that require monitoring are flagged for the next scheduled visit.",
      },
    ],
    relatedIds: ["waterproofing", "construction", "pergolas"],
  },
  {
    id: "equipment-rentals",
    title: "Equipment Rentals",
    subtitle: "Equipment available across Dubai",
    desc: "Excavators, dewatering pumps, concrete pumps, cranes, compactors and more. Daily, weekly, or monthly hire with delivery to your site.",
    heroDescription: "Construction equipment hire in Dubai. Excavators, pumps, cranes and specialist plant — delivered to your site, available daily, weekly, or monthly.",
    image: "/assets/images/excavation.jpg",
    tags: ["Excavators", "Pumps", "Cranes", "Generators", "Delivery"],
    ctaHref: "/rentals",
    summary: [
      "Al Hadeeqa offers a full fleet of construction equipment for hire across Dubai. Excavators, dewatering pumps, concrete pumps, cranes, compactors, generators, dump trucks, and scaffolding — available daily, weekly, or monthly.",
      "All equipment is delivered to your site and collected when you're done. Trained operators are available with select equipment. Contact us for a rate card.",
    ],
    inclusions: [
      "Excavators (mini, midi and full-size)",
      "Dewatering and submersible pump systems",
      "Concrete line pumps and boom pumps",
      "Mobile cranes and lifting equipment",
      "Compactors, generators, dump trucks and scaffolding",
    ],
    idealFor: [
      "Construction sites needing short-term plant hire",
      "Projects requiring delivery and on-site setup",
      "Teams needing flexible daily, weekly or monthly periods",
      "Contractors who need operators supplied with equipment",
    ],
    relatedIds: ["dewatering", "excavation", "construction"],
  },
  {
    id: "glass-aluminium",
    title: "Glass & Aluminium Solutions",
    subtitle: "Seven categories, one team",
    desc: "Custom glass and aluminium installations for homes and businesses across Dubai. Shower enclosures, mirrors, staircases, doors, windows, partitions, pergolas, and carports.",
    heroDescription: "Custom glass and aluminium installations for homes and businesses across Dubai. Shower enclosures, mirrors, staircases, doors, windows, partitions, pergolas, and carports — supplied and installed by our in-house team.",
    metaTitle: "Glass & Aluminium Solutions Dubai — Showers, Mirrors, Doors, Windows | Al Hadeeqa Contracting",
    metaDescription: "Custom glass and aluminium installations in Dubai. Shower enclosures, mirrors, staircases, doors, windows, partitions, pergolas, and carports. Free site assessment. Al Hadeeqa Contracting.",
    image: "/assets/images/glass-aluminium/partition-office-light.png",
    tags: ["Shower Enclosures", "Mirrors", "Staircases", "Doors", "Windows", "Partitions", "Pergolas & Carports"],
    sidebarNote: "Al Hadeeqa delivers this service through its specialist glass and aluminium fabrication partner. All installations are project-managed and installed by Al Hadeeqa's own licensed team.",
    brochureUrl: "/assets/AlHadeeqa-Glass-Aluminium-Brochure.pdf",
    videoUrl: "/assets/images/glass-aluminium/aluminium-structure-walkthrough-2.mp4",
    gallery: [
      "/assets/images/glass-aluminium/staircase-balustrade-led.png",
      "/assets/images/glass-aluminium/shower-enclosure-black-marble.png",
      "/assets/images/glass-aluminium/partition-office-dark.png",
      "/assets/images/glass-aluminium/carport-flat-roof-evening.png",
      "/assets/images/glass-aluminium/pergola-louvred-dubai-sunset.jpeg",
      "/assets/images/glass-aluminium/pergola-flat-led-white.jpeg",
    ],
    summary: [
      "Al Hadeeqa delivers glass and aluminium installations to a construction standard — not a shopfitting standard. Through our specialist fabrication partnership, we supply and install seven categories of glass and aluminium work: shower enclosures, mirrors, staircases and balustrades, doors and entryways, windows and facades, office partitions, and outdoor structures including pergolas and carports. Every installation is surveyed, fabricated to exact measurements, and installed by our in-house team.",
      "We use tempered safety glass, laminated glass, and double-glazed units, specifying the correct grade for each application. All aluminium frames are powder-coated to any colour and engineered for the UAE climate.",
    ],
    inclusions: [
      "Frameless and decorative shower enclosures (clear, frosted, tinted, etched glass)",
      "Custom mirrors — statement, LED-backlit, decorative wall panels, bevelled designs",
      "Glass staircases and balustrades — frameless spigot, U-channel, stainless steel post systems",
      "Aluminium and glass doors — hinged, sliding, folding, pivot, arched",
      "Aluminium windows — casement, sliding, top-hung, arched, curtain-wall facades",
      "Office and commercial glass partitions — frameless and framed systems",
      "Aluminium pergolas — fixed, louvered, motorized designs",
      "Carports — single, double, multi-bay in aluminium and steel",
      "Mashrabiya panels in laser-cut aluminium",
    ],
    idealFor: [
      "Villa owners wanting shower, mirror, staircase, or door upgrades",
      "New villa builds requiring full glass and aluminium fit-out",
      "Office and commercial tenants needing partition systems",
      "Properties wanting outdoor structures (pergolas, carports, mashrabiya)",
      "Developers requiring facade, window, and balustrade packages",
    ],
    serviceCardsHeading: "Glass & aluminium installations we build.",
    serviceCards: [
      {
        title: "Shower Enclosures",
        eyebrow: "Frameless, semi-frameless, and decorative glass",
        description: "Custom-made shower enclosures in clear, frosted, tinted, and decoratively etched glass. Fully frameless, semi-frameless, and framed options — toughened safety glass with premium hardware in chrome, brushed steel, matte black, or gold.",
        bullets: [
          "Frameless glass enclosures",
          "Frosted and etched glass options",
          "Premium hardware (chrome, matte black, gold)",
          "Custom sizing to any bathroom",
        ],
        bestFor: "Bathroom renovations, new builds, and luxury villa bathrooms.",
      },
      {
        title: "Mirrors & Decorative Glass",
        eyebrow: "Bespoke mirrors for every space",
        description: "Statement mirrors, LED-backlit vanity mirrors, decorative wall panels, and antique-finish bevelled designs. Cut to any shape — for entrances, dressing rooms, dining rooms, gyms, and bathrooms.",
        bullets: [
          "Oversized statement mirrors",
          "LED-backlit vanity mirrors",
          "Decorative wall panels",
          "Bevelled and antique-finish designs",
        ],
        bestFor: "Entrances, dressing rooms, dining rooms, and luxury bathrooms.",
      },
      {
        title: "Glass Staircases & Balustrades",
        eyebrow: "Frameless glass railings, indoor and outdoor",
        description: "Toughened laminated glass balustrades for staircases, balconies, mezzanines, and pool surrounds. Frameless spigot, U-channel, and stainless steel post systems — engineered to UAE standards.",
        bullets: [
          "Frameless spigot systems",
          "U-channel base mount",
          "Stainless steel post systems",
          "Pool fencing and balcony applications",
        ],
        bestFor: "Staircases, balconies, pool surrounds, and roof terraces.",
      },
      {
        title: "Doors & Entryways",
        eyebrow: "Aluminium and glass doors for homes and offices",
        description: "Hinged, sliding, folding, and pivot doors in aluminium and glass. Single and double leaf entry doors, internal partitions, French doors, and arched designs — frosted, etched, or clear glass in any frame finish.",
        bullets: [
          "Hinged, sliding, folding, pivot",
          "Single and double leaf",
          "Arched and French door designs",
          "Frosted, etched, and clear glass",
        ],
        bestFor: "Villa entrances, internal doors, and office entryways.",
      },
      {
        title: "Windows & Facades",
        eyebrow: "Aluminium windows and curtain wall systems",
        description: "Casement, sliding, top-hung, and arched aluminium windows with single or double glazing. Tinted, reflective, low-E, and laminated glass options. Full curtain-wall facades engineered for the UAE climate.",
        bullets: [
          "Casement, sliding, top-hung windows",
          "Double glazing and low-E glass",
          "Tinted and reflective options",
          "Curtain-wall facade systems",
        ],
        bestFor: "New builds, window replacements, and commercial facades.",
      },
      {
        title: "Office & Interior Partitions",
        eyebrow: "Glass partitions for modern workspaces",
        description: "Floor-to-ceiling glass partitions in single and double glazed configurations. Frosted, clear, and decorative film options. Modular systems for offices, clinics, showrooms, and retail.",
        bullets: [
          "Frameless 12–15mm tempered glass",
          "Framed aluminium systems",
          "Acoustic and fire-rated options",
          "Full-height installation",
        ],
        bestFor: "Offices, clinics, showrooms, and commercial fit-outs.",
      },
      {
        title: "Pergolas, Carports & Outdoor Structures",
        eyebrow: "Premium outdoor aluminium structures",
        description: "Aluminium pergolas in fixed, louvered, and motorized designs. Carports in single, double, and multi-bay configurations. Mashrabiya panels in laser-cut aluminium for facades and privacy walls. All powder-coated to any colour and engineered for UAE conditions.",
        bullets: [
          "Fixed, louvered, motorized pergolas",
          "Single and multi-bay carports",
          "Mashrabiya privacy panels",
          "Powder-coated to any colour",
        ],
        bestFor: "Villa gardens, patios, driveways, and outdoor entertaining areas.",
      },
    ],
    trustHeading: "Why construction-grade glass and aluminium installation matters.",
    trustPoints: [
      {
        title: "Glass specified for the application",
        body: "Outdoor enclosures, pool balustrades, and office partitions all require different glass specifications. We specify the correct grade for every application.",
      },
      {
        title: "Construction-grade installation",
        body: "Every installation is surveyed, fabricated to exact measurements, and detailed at every edge and joint.",
      },
      {
        title: "In-house project management",
        body: "All work is managed and installed by Al Hadeeqa's licensed team. No subcontracting.",
      },
      {
        title: "UAE climate engineering",
        body: "All aluminium is powder-coated and all glass is specified for Dubai's heat, UV, and wind loads.",
      },
    ],
    relatedIds: ["glassrooms", "pergolas", "carports"],
  },
];

export const SERVICE_SELECT_OPTIONS = [
  ...SERVICES.flatMap((service) =>
    service.id === "waterproofing"
      ? [service.title, "Bathroom / Wet Area Waterproofing", "Metal Roof Waterproofing"]
      : service.id === "equipment-rentals"
      ? ["Equipment Rental"]
      : [service.title]
  ),
  "Underground Bunker",
  "Other",
];

export function getServiceAnchorId(serviceId) {
  return `service-${serviceId}`;
}

export function getServiceHref(serviceId) {
  return `/services/${serviceId}`;
}

export function getServiceById(serviceId) {
  return SERVICES.find((service) => service.id === serviceId) || null;
}
